import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import {
  DEFAULT_SEARCH_BAR_FILTERS,
  ESearchBarMode,
  ESearchBarStatus,
  SEARCH_LIMIT,
  SearchBarFilters,
  SearchResult,
  RecentSearchResult,
  getSearchIdentity,
} from './types';
import { SearchDetailDTO } from 'business/models/Models';

type SearchBarContextValues = {
  filters: SearchBarFilters;
  setFilters: (filters: SearchBarFilters) => void;
  resetFilters: () => void;
  updateFilters: (update: Partial<SearchBarFilters>) => void;
  commitFilters: () => void;
  discardFilterChanges: () => void;
  openAdvancedSearch: () => void;
  closeAdvancedSearch: () => void;
  mode: ESearchBarMode;
  setMode: (mode: ESearchBarMode) => void;
  isPanelOpen: boolean;
  setIsPanelOpen: (isOpen: boolean) => void;
  status: ESearchBarStatus;
  results: SearchResult[];
  updateSearch: (status: ESearchBarStatus, results?: SearchResult[]) => void;
  explorerStatus: ESearchBarStatus;
  explorerResults: SearchResult[];
  explorerMode: ESearchBarMode;
  updateExplorerSearch: (
    status: ESearchBarStatus,
    results?: SearchResult[],
    mode?: ESearchBarMode,
  ) => void;
  clearSearch: () => void;
  recentResults: RecentSearchResult[];
  updateRecentResults: (results?: RecentSearchResult[]) => void;
  recordRecentSearch: (searchDetail: SearchDetailDTO) => void;
};

const SearchBarContext = createContext<SearchBarContextValues | null>(null);

export const SearchBarProvider = ({
  children,
  initialFilters = DEFAULT_SEARCH_BAR_FILTERS,
}: PropsWithChildren<{ initialFilters?: SearchBarFilters }>) => {
  const [filters, setFilters] = useState<SearchBarFilters>(initialFilters);
  const [committedFilters, setCommittedFilters] =
    useState<SearchBarFilters>(initialFilters);
  const [mode, setModeState] = useState<ESearchBarMode>(ESearchBarMode.Basic);
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [status, setStatus] = useState<ESearchBarStatus>(ESearchBarStatus.NoResults);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [explorerStatus, setExplorerStatus] = useState<ESearchBarStatus>(
    ESearchBarStatus.Empty,
  );
  const [explorerResults, setExplorerResults] = useState<SearchResult[]>([]);
  const [explorerMode, setExplorerMode] = useState<ESearchBarMode>(ESearchBarMode.Basic);
  const [recentResults, setRecentResults] = useState<RecentSearchResult[]>([]);
  const updateFilters = useCallback((update: Partial<SearchBarFilters>) => {
    setFilters((prev) => ({ ...prev, ...update }));
  }, []);

  const setMode = useCallback(
    (newMode: ESearchBarMode) => {
      if (newMode === mode) {
        return;
      }
      setModeState(newMode);
    },
    [mode],
  );

  const resetFilters = useCallback(() => setFilters(DEFAULT_SEARCH_BAR_FILTERS), []);

  const commitFilters = useCallback(() => {
    setCommittedFilters(filters);
  }, [filters]);

  const discardFilterChanges = useCallback(() => {
    setFilters(committedFilters);
  }, [committedFilters]);

  const openAdvancedSearch = useCallback(() => {
    setCommittedFilters(filters);
    setModeState(ESearchBarMode.Advanced);
  }, [filters]);

  const closeAdvancedSearch = useCallback(() => {
    setFilters(committedFilters);
    setModeState(ESearchBarMode.Basic);
  }, [committedFilters]);

  const clearSearch = useCallback(() => {
    setFilters(DEFAULT_SEARCH_BAR_FILTERS);
    setCommittedFilters(DEFAULT_SEARCH_BAR_FILTERS);
    setStatus(ESearchBarStatus.Empty);
    setResults([]);
    setExplorerStatus(ESearchBarStatus.Empty);
    setExplorerResults([]);
    setExplorerMode(ESearchBarMode.Basic);
  }, []);

  const updateSearch = useCallback(
    (status: ESearchBarStatus, results?: SearchResult[]) => {
      setStatus(status);
      if (results != null) {
        setResults(results);
      }
    },
    [],
  );

  const updateExplorerSearch = useCallback(
    (status: ESearchBarStatus, results?: SearchResult[], mode?: ESearchBarMode) => {
      setExplorerStatus(status);
      if (results != null) {
        setExplorerResults(results);
      }
      if (mode != null) {
        setExplorerMode(mode);
      }
    },
    [],
  );

  const updateRecentResults = useCallback((result: RecentSearchResult[]) => {
    setRecentResults(result);
  }, []);

  const recordRecentSearch = useCallback((searchDetail: SearchDetailDTO) => {
    const identity = getSearchIdentity(searchDetail);
    if (!identity) {
      return;
    }
    setRecentResults((prev) => {
      const existing = prev.find((r) => getSearchIdentity(r.searchDetail) === identity);
      const newEntry: RecentSearchResult = {
        id: existing?.id ?? -Date.now(),
        searchDetail,
        searchDate: new Date().toISOString(),
      };
      const withoutMatch = prev.filter(
        (r) => getSearchIdentity(r.searchDetail) !== identity,
      );
      return [newEntry, ...withoutMatch].slice(0, SEARCH_LIMIT);
    });
  }, []);

  return (
    <SearchBarContext.Provider
      value={{
        filters,
        setFilters,
        resetFilters,
        updateFilters,
        commitFilters,
        discardFilterChanges,
        openAdvancedSearch,
        closeAdvancedSearch,
        mode,
        setMode,
        isPanelOpen,
        setIsPanelOpen,
        status,
        results,
        updateSearch,
        explorerStatus,
        explorerResults,
        explorerMode,
        updateExplorerSearch,
        clearSearch,
        recentResults,
        updateRecentResults,
        recordRecentSearch,
      }}
    >
      {children}
    </SearchBarContext.Provider>
  );
};

export const useSearchBarContext = () => {
  const ctx = useContext(SearchBarContext);
  if (!ctx) {
    throw new Error('useSearchBarContext must be used within SearchBarContext.');
  }
  return ctx;
};
